import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompetencesGeneriquesStepRequest, saveAllCompetencesGeneriquesAutoEvalRequest } from "../../../../redux/actions/performanceActions";
import { asArray, Field, StepState } from "../performanceUi";

export default function CompetencesGeneriquesStep({ progressId }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.competencesGeneriques);
    const data = slice.stepByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const competences = asArray(data?.competences);
    const evaluations = asArray(data?.evaluations);
    const [drafts, setDrafts] = useState({});
    const [selectedDetailCompetence, setSelectedDetailCompetence] = useState(null);

    useEffect(() => {
        dispatch(fetchCompetencesGeneriquesStepRequest(progressId));
    }, [dispatch, progressId]);

    const byCompetence = useMemo(() => {
        return evaluations.reduce((acc, evaluation) => {
            if (evaluation.typeEvaluation === "AUTO_EVALUATION") acc[evaluation.competenceGeneriqueId] = evaluation;
            return acc;
        }, {});
    }, [evaluations]);

    const managerByCompetence = useMemo(() => {
        return evaluations.reduce((acc, evaluation) => {
            if (evaluation.typeEvaluation === "MANAGER_EVALUATION") acc[evaluation.competenceGeneriqueId] = evaluation;
            return acc;
        }, {});
    }, [evaluations]);

    const managerValidated = data?.managerEvaluationCompleted || data?.stepStatus === "COMPLETED";
    const hasAutoEvaluation = evaluations.some((evaluation) => evaluation.typeEvaluation === "AUTO_EVALUATION");
    const statusLabel = managerValidated ? "Validee par manager" : hasAutoEvaluation ? "En attente validation manager" : "A saisir";

    const updateDraft = (id, patch) => setDrafts((current) => ({ ...current, [id]: { ...(current[id] || {}), ...patch } }));

    const saveAll = () => {
        if (managerValidated) return;
        const items = competences
            .filter((competence) => !managerByCompetence[competence.id]?.id)
            .map((competence) => {
                const previous = byCompetence[competence.id] || {};
                const draft = drafts[competence.id] || {};
                return {
                    evaluationProgressId: Number(progressId),
                    competenceGeneriqueId: competence.id,
                    niveauEvalue: draft.niveauEvalue ?? previous.niveauEvalue,
                    commentaire: draft.commentaire ?? previous.commentaire ?? "",
                };
            });
        if (items.some((item) => !item.niveauEvalue)) {
            window.alert("Veuillez saisir un niveau pour chaque competence.");
            return;
        }
        dispatch(saveAllCompetencesGeneriquesAutoEvalRequest(Number(progressId), items));
    };

    return (
        <StepState loading={loading} error={error} empty={!data || competences.length === 0}>
            {data?.blockedReason && <div className="perf-state perf-state--warning">{data.blockedReason}</div>}
            <div className={`perf-state perf-state--inline ${managerValidated ? "" : "perf-state--warning"}`}>{statusLabel}</div>
            <div className="perf-actions perf-actions--top">
                <button className="perf-btn perf-btn--primary" type="button" disabled={managerValidated || competences.length === 0} onClick={saveAll}>
                    {managerValidated ? "Validation manager effectuee" : "Enregistrer toutes les competences"}
                </button>
            </div>
            <div className="perf-cg-grid">
                {competences.map((competence) => {
                    const previous = byCompetence[competence.id] || {};
                    const managerEvaluation = managerByCompetence[competence.id] || {};
                    const competenceLocked = managerValidated || Boolean(managerEvaluation.id);
                    const draft = drafts[competence.id] || {};
                    const maxLevel = competence.level || previous.niveauMax || 4;

                    const scoreValue = draft.niveauEvalue ?? previous.niveauEvalue ?? "";
                    const commentValue = draft.commentaire ?? previous.commentaire ?? "";

                    const getStatusText = () => {
                        if (managerEvaluation.id) return "✓ Validé";
                        return scoreValue ? "Évalué" : "Non évalué";
                    };

                    return (
                        <div className="perf-cg-card" key={competence.id}>
                            <div>
                                <h3 className="perf-cg-card-title">{competence.nom}</h3>
                                
                                <div className="perf-cg-score-row">
                                    <input 
                                        type="number" 
                                        min="1" 
                                        max={maxLevel} 
                                        className="perf-cg-score-input"
                                        disabled={competenceLocked}
                                        value={scoreValue}
                                        onChange={(event) => updateDraft(competence.id, { niveauEvalue: event.target.value })} 
                                    />
                                    <span className="perf-cg-score-max">/ {maxLevel}</span>
                                </div>

                                <div className="perf-cg-comment-block">
                                    <span className="perf-cg-comment-label">Commentaire</span>
                                    <textarea 
                                        className="perf-cg-comment-textarea"
                                        disabled={competenceLocked}
                                        value={commentValue}
                                        placeholder="Décrivez votre niveau..."
                                        onChange={(event) => updateDraft(competence.id, { commentaire: event.target.value })}
                                    />
                                </div>
                            </div>

                            {managerEvaluation.id && (
                                <div className="perf-cg-facts" style={{ marginTop: "8px", marginBottom: "8px" }}>
                                    <div className="perf-cg-fact-item">
                                        <span className="perf-cg-fact-label">Validation manager</span>
                                        <span className="perf-cg-fact-value">{managerEvaluation.niveauEvalue} / {maxLevel}</span>
                                    </div>
                                    {managerEvaluation.commentaire && (
                                        <div style={{ color: "var(--perf-muted)", marginTop: "4px", fontSize: "10px" }}>
                                            {managerEvaluation.commentaire}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="perf-cg-footer">
                                <span className="perf-cg-status">{getStatusText()}</span>
                                <button 
                                    type="button" 
                                    className="perf-cg-details-btn"
                                    onClick={() => setSelectedDetailCompetence(competence)}
                                >
                                    Détails
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {selectedDetailCompetence && (
                <div className="perf-modal-overlay" onClick={() => setSelectedDetailCompetence(null)}>
                    <div className="perf-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="perf-modal__header">
                            <h2>{selectedDetailCompetence.nom}</h2>
                            <button className="perf-modal__close" onClick={() => setSelectedDetailCompetence(null)}>&times;</button>
                        </div>
                        <div className="perf-modal__body">
                            <p style={{ fontSize: "13px", color: "var(--perf-muted)", lineHeight: "1.6", margin: 0 }}>
                                {selectedDetailCompetence.description || "Aucune description disponible pour cette compétence."}
                            </p>
                            {selectedDetailCompetence.categorie && (
                                <div style={{ marginTop: "12px" }}>
                                    <span className="perf-badge perf-badge--blue">{selectedDetailCompetence.categorie}</span>
                                </div>
                            )}
                        </div>
                        <div className="perf-modal__footer">
                            <button className="perf-btn" onClick={() => setSelectedDetailCompetence(null)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}
        </StepState>
    );
}
